import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import type { Thread } from "@/api/types";
import { useStore } from "@/store/StoreContext";
import { Badge, Button, Input } from "@/components/ui";
import { MessageThread } from "@/components/MessageThread";
import { Pager } from "@/components/Pager";
import { fmtDateTime } from "@/lib/dates";

const PAGE = 50;

/** Inbox: every customer conversation of the brand, unread first. */
export function MessagesPage() {
  const { store } = useStore();
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selected, setSelected] = useState<Thread | null>(null);
  const [page, setPage] = useState(0);
  const brandId = store?.brand_id;
  const threads = useQuery({
    queryKey: ["threads", brandId, q, unreadOnly, page],
    queryFn: () => api<Thread[]>(`/app/threads?brand_id=${brandId}&limit=${PAGE + 1}&offset=${page * PAGE}${unreadOnly ? "&unread_only=true" : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
    enabled: !!brandId, refetchInterval: 30000,
  });
  const status = useQuery({ queryKey: ["messaging-status", brandId], queryFn: () => api<{ available: boolean; provider: string | null }>(`/app/messaging/status?brand_id=${brandId}`), enabled: !!brandId });
  if (!store || !brandId) return <p className="text-muted">{t("app.loading")}</p>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-semibold mr-2">{t("nav.messages")}</h1>
        <Input className="!w-64" placeholder={t("appt.searchCustomer")} value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={unreadOnly} onChange={(e) => { setUnreadOnly(e.target.checked); setPage(0); }} /> {t("msg.unreadOnly")}</label>
        {status.data && !status.data.available && <span className="text-xs text-warn">{t("msg.noProvider")}</span>}
      </div>
      <div className="grid gap-3 lg:grid-cols-[340px_1fr]">
        <div className="space-y-2"><ul className="max-h-[70vh] overflow-auto rounded-xl border bg-surface divide-y">
          {threads.data?.length === 0 && <li className="p-3 text-sm text-muted">{t("msg.noThreads")}</li>}
          {(threads.data ?? []).slice(0, PAGE).map((th) => (
            <li key={th.id}>
              <button type="button" className={`w-full p-3 text-left hover:bg-surface-2 ${selected?.id === th.id ? "bg-accent-soft" : ""}`} onClick={() => setSelected(th)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{th.customer.first_name} {th.customer.last_name}</span>
                  {th.unread_count > 0 && <Badge tone="blue">{th.unread_count}</Badge>}
                </div>
                <div className="truncate text-xs text-muted">{th.last_snippet ?? ""}</div>
                <div className="text-[11px] text-faint">{th.last_message_at ? fmtDateTime(th.last_message_at, i18n.language) : ""}</div>
              </button>
            </li>
          ))}
        </ul><Pager page={page} pageSize={PAGE} count={threads.data?.length ?? 0} onPage={setPage} /></div>
        <div className="rounded-xl border bg-surface p-3">
          {!selected && <p className="text-sm text-muted">{t("msg.pickThread")}</p>}
          {selected && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div><span className="font-medium">{selected.customer.first_name} {selected.customer.last_name}</span>{selected.customer.phone && <span className="text-sm text-muted"> · {selected.customer.phone}</span>}</div>
                <Button variant="ghost" onClick={() => setSelected(null)}>{t("app.cancel")}</Button>
              </div>
              <MessageThread brandId={brandId} customerId={selected.customer.id} canSend={status.data?.available ?? true} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
